import { useEffect, useState } from 'react';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
} from 'recharts';
import { useFund } from '../context/FundContext';
import FundDateBar from '../components/FundDateBar';
import { Loading } from '../components/Loader';
import { api } from '../api';
import { money } from '../format';

function InvCard({ label, value, tone }) {
  return (
    <div className={`inv-card ${tone || ''}`}>
      <div className="inv-card-label">{label}</div>
      <div className="inv-card-value">{value}</div>
    </div>
  );
}

export default function Investor() {
  const { fund, date } = useFund();
  const [d, setD] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!fund || !date) return undefined;
    let active = true;
    setLoading(true);
    setError(null);
    api.investor(fund, date)
      .then((data) => { if (active) { setD(data); setError(null); } })
      .catch((e) => { if (active) { setError(e.message); setD(null); } })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [fund, date]);

  const c = d?.cards;

  return (
    <div>
      <FundDateBar />
      {error && <div className="error">Error: {error}</div>}
      {loading && <Loading label="Loading investor summary…" />}

      {d && !loading && (
        <>
          <div className="notice">
            Class-level capital summary. Current Balance and Total Redemptions are exact;
            Total Additions and Total Income are derived from the daily performance series
            (the initial subscription predates the series, so it is approximated from the
            opening balance).
          </div>

          {d.hasData ? (
            <>
              <div className="inv-grid">
                <InvCard label="Current Balance (USD)" value={money(c.currentBalance)} />
                <InvCard label="Total Additions (USD)" value={money(c.totalAdditions)} tone="teal" />
                <InvCard label="Total Redemptions (USD)" value={money(c.totalRedemptions)} tone="teal" />
                <InvCard
                  label="Total Income (USD)"
                  value={money(c.totalIncome)}
                  tone={c.totalIncome >= 0 ? 'teal' : 'red'}
                />
              </div>

              <div className="panel">
                <h3>VAMI <span className="muted-inline">Index (Base 1000) · {d.seriesStart} → {d.seriesEnd}</span></h3>
                <ResponsiveContainer width="100%" height={320}>
                  <LineChart data={d.vamiSeries.map((x) => ({ date: x.date, vami: x.vami }))}
                    margin={{ top: 10, right: 20, left: 0, bottom: 4 }}>
                    <XAxis dataKey="date" fontSize={11} minTickGap={40} />
                    <YAxis fontSize={11} width={60} domain={['auto', 'auto']} />
                    <Tooltip formatter={(v) => Number(v).toFixed(2)} />
                    <Line type="monotone" dataKey="vami" stroke="#2bb7e6" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </>
          ) : (
            <div className="panel">
              <p className="muted">No capital / performance data for this fund as of {date}.</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
